/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, DatePicker, Spin, message, Button, Tooltip, Select, Modal, InputNumber, Checkbox, Input } from 'antd';
import moment from 'moment';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styles from './styles';
import {
  getAdminKpis,
  getDeliveriesByRoute,
  getAdminRecent,
  getAdminDeliveriesByDay,
  getAdminTopLocations,
  getAdminTopCustomers,
  getAdminOrdersInRange,
  getAdminReturns,
  getVehicleMaintenance,
  getDriverLicenses,
  updateVehicleMileage,
} from '../../_shared/services/admin-dashboard.service';
import { getLifecycleReport } from '../../_shared/services/reports.services';
import { getReconList } from '../../_shared/services/orders.service';
import { buildAdminExportRows, buildReconMap } from '../reports/admin-order-export';
import { getStatusIcon } from '../../models/order';
import { OrderModal } from '../orders/orders.component';
import { getUserData } from '../../authentication/authentication.actions';
import { exportToCsv, getConfig, getManifestUrl, getReconUrl, orderHasManifest, searchTable } from '../../actions/utils';
import { getFleetData, updateFleetData, updateVehicle } from '../../_shared/services/vehicles.service';
import { MaintForm, MaintTypes } from '../fleet-manager/maint-form';
import {
  WEEKDAY_LABELS,
  dowKey,
  loadDays,
  saveDays,
  fmtDate,
  fmtTick,
  truncate,
  computeWeekdayAverages,
  getRejectionReason,
} from '../../_shared/dashboard-helpers';

// Top range defaults to the Current Day (beginning to end of day) per
// Danielle 2026-05-20 — was previously current week + following week.
const TOP_RANGE_DEFAULT = () => [
  moment().startOf('day'),
  moment().endOf('day'),
];
const BOTTOM_RANGE_DEFAULT = () => [
  moment().subtract(3, 'weeks').startOf('isoWeek'),
  moment().endOf('isoWeek'),
];

// Tile order — Joel 2026-05-26 annotated screenshot: top row is now
// Outgoing Orders | Orders Out For Delivery | Scheduled Pickups | Scheduled
// Drop Offs so that the natural lifecycle (Outgoing → Out For Delivery)
// reads left-to-right. Bottom row unchanged.
// Each row is forced to exactly four cards by sizing them at md={6} (24 / 6 = 4).
const KPI_DEFS = [
  // Top row
  { key: 'outgoingOrders', title: 'Outgoing Orders' },
  { key: 'outForDelivery', title: 'Orders Out for Delivery' },
  { key: 'scheduledPickups', title: 'Scheduled Pickups' },
  { key: 'scheduledDropOffs', title: 'Scheduled Drop Offs' },
  // Bottom row
  { key: 'delivered', title: 'Orders Delivered' },
  { key: 'rejected', title: 'Orders Rejected' },
  { key: 'pickedUp', title: 'Orders Picked Up' },
  { key: 'checkedIn', title: 'Orders Checked In' },
];

// Default visible line counts per report, with full counts available via the
// per-section "Show all" toggle.
const DEFAULT_LIMITS = {
  completed: 10,
  rejected: 5,
  exceptions: 5,
  cashReturns: 5,
  rejectionReturns: 5,
  scheduledPaymentPickups: 5,
};

const REJECTION_TEXT_LIMIT = 30;
const TOP_LOCATIONS_LIMIT = 20;
const TOP_CUSTOMERS_LIMIT = 20;
const CHART_PX_PER_DAY = 70;
const CHART_VISIBLE_DAYS = 7;

const getExceptionReason = row => get(row, 'data.order.exception', '') || '';

export const AdminDashboard = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const userId = get(user, 'id');
  const userEmail = get(user, 'email');

  useEffect(() => {
    dispatch(getUserData());
  }, [dispatch]);

  const [topRange, setTopRange] = useState(TOP_RANGE_DEFAULT());
  const [bottomRange, setBottomRange] = useState(BOTTOM_RANGE_DEFAULT());
  const [topLoading, setTopLoading] = useState(false);
  const [recentLoading, setRecentLoading] = useState(false);
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [bottomLoading, setBottomLoading] = useState(false);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [driverLicensesLoading, setDriverLicensesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);

  const [counts, setCounts] = useState({});
  const [routeRows, setRouteRows] = useState([]);
  const [recent, setRecent] = useState({ completed: [], rejected: [], exceptions: [] });
  const [returns, setReturns] = useState({ cashReturns: [], rejectionReturns: [], scheduledPaymentPickups: [] });
  const [daySeries, setDaySeries] = useState([]);
  const [topLocations, setTopLocations] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [vehicleRows, setVehicleRows] = useState([]);
  const [intervals, setIntervals] = useState({});
  const [driverLicenseRows, setDriverLicenseRows] = useState([]);

  // Day-of-week selections persist per-user via localStorage (Danielle
  // 2026-06-03 — so Sat/Sun stay deselected across sessions). Keys are
  // namespaced by userId + dashboard + chart.
  const lineDaysKey = dowKey(userId, 'admin', 'line');
  const avgDaysKey = dowKey(userId, 'admin', 'avg');
  const [lineDays, setLineDaysState] = useState(() => loadDays(lineDaysKey));
  const [avgDays, setAvgDaysState] = useState(() => loadDays(avgDaysKey));
  const setLineDays = days => {
    setLineDaysState(days);
    saveDays(lineDaysKey, days);
  };
  const setAvgDays = days => {
    setAvgDaysState(days);
    saveDays(avgDaysKey, days);
  };
  // userId may be absent on the very first render (before persisted/refreshed
  // user data is available); re-sync the saved selections once it resolves.
  useEffect(() => {
    setLineDaysState(loadDays(lineDaysKey));
    setAvgDaysState(loadDays(avgDaysKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  // All / Internal / External order filter (shown only when Internal
  // Distribution is enabled). 'all' => no server-side orderDef filter.
  const internalDistEnabled = getConfig('orders.isInternalDist', false);
  const [orderDef, setOrderDef] = useState('all');
  const orderDefParam = orderDef === 'all' ? undefined : orderDef;

  // Per-section visibility (Edit Settings → Dashboard → Admin Dashboard Hidden
  // Sections). Empty list = show everything.
  const adminHiddenSections = getConfig('dashboard.admin.hiddenSections', []) || [];
  // Master Rejection Returns toggle (Edit Settings → Orders → Enable Rejection
  // Returns). Default on; when off, the Rejection Returns Scheduled section is
  // hidden regardless of the hidden-sections list.
  const rejectionReturnsEnabled = getConfig('orders.rejectionReturns.enabled', true);
  // Card #242 — master switch for driver licence expiration tracking. Gates
  // this widget AND the reminder emails. Default OFF, and the fallback here is
  // byte-identical to the server's getConfigProp() call in
  // server/src/controllers/driverlicenses.js — settingsConfig.json's
  // defaultValue never reaches the server, so the call site IS the default.
  // `|| false` because a full-document settings save can write null, which
  // lodash get() does NOT replace with the fallback.
  const licenseExpiryEnabled = !!(getConfig('licenseExpiry.enabled', false) || false);
  const showAdmin = key => !adminHiddenSections.includes(key)
    && !(key === 'rejectionReturns' && !rejectionReturnsEnabled);

  // Card #277 — one term, applied to every report table on the page.
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState({
    completed: false,
    rejected: false,
    exceptions: false,
    cashReturns: false,
    rejectionReturns: false,
    scheduledPaymentPickups: false,
  });

  // Reconciliation Returns container filter (Danielle 2026-05-20). Filters the
  // Reconciliation Returns Scheduled table client-side by Customer Pickup flag.
  //   'returns'         = orders NOT flagged customerPickup (default)
  //   'customerPickups' = orders flagged customerPickup
  //   'all'             = no filter
  const [reconReturnFilter, setReconReturnFilter] = useState('returns');
  // Rejection Returns container filter (Joel 2026-05-21). Operates identically
  // to the Reconciliation Returns dropdown above — filters the Rejection
  // Returns Scheduled table client-side by Customer Pickup flag.
  //   'returns'         = orders NOT flagged customerPickup (default)
  //   'customerPickups' = orders flagged customerPickup
  //   'all'             = no filter
  const [rejReturnFilter, setRejReturnFilter] = useState('returns');

  const [editModal, setEditModal] = useState(undefined);
  const [mileageModal, setMileageModal] = useState({ visible: false, vehicle: null, value: null });
  const [maintModal, setMaintModal] = useState({ visible: false });

  const reversedSeries = useMemo(() => [...(daySeries || [])].reverse(), [daySeries]);
  const weekdayAverages = useMemo(() => computeWeekdayAverages(daySeries), [daySeries]);

  const lineSeriesFiltered = useMemo(() => {
    if (!lineDays.length || lineDays.length === WEEKDAY_LABELS.length) return reversedSeries;
    return reversedSeries.filter(row => {
      const idx = moment(row.date).isoWeekday() - 1;
      return idx >= 0 && idx <= 6 && lineDays.includes(WEEKDAY_LABELS[idx]);
    });
  }, [reversedSeries, lineDays]);

  const avgFiltered = useMemo(() => {
    if (!avgDays.length || avgDays.length === WEEKDAY_LABELS.length) return weekdayAverages;
    return weekdayAverages.filter(b => avgDays.includes(b.day));
  }, [weekdayAverages, avgDays]);

  const loadTop = useCallback(async () => {
    setTopLoading(true);
    try {
      const [k, r] = await Promise.all([
        getAdminKpis({ range: topRange, orderDef: orderDefParam }),
        getDeliveriesByRoute({ range: topRange, orderDef: orderDefParam }),
      ]);
      setCounts(k);
      setRouteRows(r);
    } catch (err) {
      message.error(`Failed to load dashboard: ${err.message}`);
    } finally {
      setTopLoading(false);
    }
  }, [topRange, orderDefParam]);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    try {
      const r = await getAdminRecent({ range: topRange, completedLimit: 500, rejectedLimit: 500, exceptionLimit: 500, orderDef: orderDefParam });
      setRecent(r);
    } catch (err) {
      message.error(`Failed to load recent orders: ${err.message}`);
    } finally {
      setRecentLoading(false);
    }
  }, [topRange, orderDefParam]);

  const loadReturns = useCallback(async () => {
    setReturnsLoading(true);
    try {
      const r = await getAdminReturns({ range: topRange, limit: 500, orderDef: orderDefParam });
      setReturns(r);
    } catch (err) {
      message.error(`Failed to load scheduled returns: ${err.message}`);
    } finally {
      setReturnsLoading(false);
    }
  }, [topRange, orderDefParam]);

  const loadBottom = useCallback(async () => {
    setBottomLoading(true);
    try {
      const [s, l, c] = await Promise.all([
        getAdminDeliveriesByDay({ range: bottomRange, orderDef: orderDefParam }),
        getAdminTopLocations({ range: bottomRange, limit: TOP_LOCATIONS_LIMIT, orderDef: orderDefParam }),
        getAdminTopCustomers({ range: bottomRange, limit: TOP_CUSTOMERS_LIMIT, orderDef: orderDefParam }),
      ]);
      setDaySeries(s);
      setTopLocations(l);
      setTopCustomers(c);
    } catch (err) {
      message.error(`Failed to load lower dashboard: ${err.message}`);
    } finally {
      setBottomLoading(false);
    }
  }, [bottomRange, orderDefParam]);

  const loadVehicles = useCallback(async () => {
    setVehiclesLoading(true);
    try {
      const { rows, intervals: ints } = await getVehicleMaintenance();
      setVehicleRows(rows);
      setIntervals(ints);
    } catch (err) {
      message.error(`Failed to load vehicles: ${err.message}`);
    } finally {
      setVehiclesLoading(false);
    }
  }, []);

  const loadDriverLicenses = useCallback(async () => {
    if (!licenseExpiryEnabled) { setDriverLicenseRows([]); return; }
    setDriverLicensesLoading(true);
    try {
      const { rows } = await getDriverLicenses();
      setDriverLicenseRows(rows);
    } catch (err) {
      message.error(`Failed to load driver licenses: ${err.message}`);
    } finally {
      setDriverLicensesLoading(false);
    }
  }, [licenseExpiryEnabled]);

  useEffect(() => { loadTop(); }, [loadTop]);
  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => { loadReturns(); }, [loadReturns]);
  useEffect(() => { loadBottom(); }, [loadBottom]);
  useEffect(() => { loadVehicles(); }, [loadVehicles]);
  useEffect(() => { loadDriverLicenses(); }, [loadDriverLicenses]);

  const refreshAll = () => {
    loadTop();
    loadRecent();
    loadReturns();
    loadBottom();
    loadVehicles();
    loadDriverLicenses();
  };

  const openOrder = record => setEditModal({ state: { editOrder: record } });
  const closeOrder = () => setEditModal(undefined);

  const openMileageModal = vehicle => setMileageModal({
    visible: true,
    vehicle,
    value: typeof vehicle.mileage === 'number' && vehicle.mileage > 0 ? vehicle.mileage : null,
  });
  const closeMileageModal = () => setMileageModal({ visible: false, vehicle: null, value: null });
  const saveMileage = async () => {
    const { vehicle, value } = mileageModal;
    if (!vehicle) return;
    if (value == null || value === '' || Number.isNaN(Number(value))) {
      message.error('Mileage must be a positive number.');
      return;
    }
    const m = parseInt(value, 10);
    if (Number.isNaN(m) || m < 0) {
      message.error('Mileage must be a positive number.');
      return;
    }
    if (m < (vehicle.mileage || 0)) {
      message.error('Mileage cannot be less than current mileage.');
      return;
    }
    try {
      await updateVehicleMileage({ vehicleId: vehicle.vehicleId, mileage: m });
      message.success('Mileage updated.');
      closeMileageModal();
      loadVehicles();
    } catch (err) {
      message.error(`Failed to update mileage: ${err.message}`);
    }
  };

  // Maintenance modal: opens inline (no navigation to Fleet Manager) and
  // pre-selects the row's vehicle when launched from a row's "+ Add Maintenance"
  // button. Shape of the submit handler mirrors fleet-manager/index.js so the
  // shared MaintForm works identically.
  const openMaintModal = (vehicleRow) => {
    // `registerForm` in `_shared/form` passes the AntD form instance directly,
    // so this ref IS the form — call `.validateFields(...)` / `.resetFields()`
    // directly. (The previous code went through `formRef.props.form.*`, which is
    // why Save / Save and Add appeared inert — Joel 2026-05-12.)
    let formRef;
    const finalize = async () => new Promise((resolve, reject) => {
      if (!formRef) {
        message.error('Form not ready');
        return reject();
      }
      formRef.validateFields(async (err, vals) => {
        if (err) return reject();
        try {
          const allVehicles = await getFleetData();
          const vehicle = (allVehicles || []).find(v => v.id === vals.vehicleId);
          vals.mileage = parseInt(vals.mileage);
          if (!vehicle) {
            message.error('Unable to find vehicle');
            return reject();
          }
          if (vehicle.mileage && vals.mileage < parseInt(vehicle.mileage)) {
            message.error('Mileage cannot be less than current mileage.');
            return reject();
          }
          const mType = MaintTypes.find(m => m.value === vals.type);
          vehicle.mileage = vals.mileage;
          if (mType && mType.change && mType.change !== 'other') {
            if (!vehicle.data) vehicle.data = {};
            vehicle.data[mType.change] = vals.mileage;
          }
          const now = moment().toISOString();
          const maint = {
            vehicleId: vals.vehicleId,
            maintDate: vals.date.toISOString(),
            createdAt: now,
            updatedAt: now,
            data: vals,
          };
          await Promise.all([
            updateFleetData({ maint }),
            updateVehicle({ ...vehicle, maint: undefined }),
          ]);
          loadVehicles();
          message.success('Maintenance has been added.');
          resolve();
        } catch (e) {
          message.error(`Failed to save maintenance: ${e.message}`);
          reject();
        }
      });
    });

    const close = () => setMaintModal(s => ({ ...s, visible: false }));

    setMaintModal({
      visible: true,
      onSubmit: finalize,
      onOk: async () => { try { await finalize(); close(); } catch (_) { /* validation failed */ } },
      onCancel: close,
      onClose: close,
      onSaveAndAdd: async () => {
        try {
          await finalize();
          if (formRef) formRef.resetFields();
        } catch (_) { /* validation failed */ }
      },
      registerForm: f => { formRef = f; },
      vehicles: vehicleRows.map(v => ({ title: `${v.name} - ${v.plate}`, value: v.vehicleId })),
      defaults: vehicleRow ? { vehicleId: vehicleRow.vehicleId } : undefined,
    });
  };

  const renderDaySelect = (value, onChange) => (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      style={{ minWidth: 160 }}
      maxTagCount={value.length === WEEKDAY_LABELS.length ? 0 : 2}
      maxTagPlaceholder={value.length === WEEKDAY_LABELS.length ? 'All' : undefined}
      size="small"
      allowClear={false}
    >
      {WEEKDAY_LABELS.map(d => (
        <Select.Option key={d} value={d}>{d}</Select.Option>
      ))}
    </Select>
  );

  const renderManifest = (_, row) => {
    const num = get(row, 'data.billing.manifest', '') || '';
    if (!num) return '';
    const { file } = orderHasManifest(row);
    const url = getManifestUrl(file);
    if (!url) return num;
    return (
      <a css={css(styles.orderLink)} href={url} target="_blank" rel="noopener noreferrer">{num}</a>
    );
  };

  const baseColumns = [
    {
      title: 'Order ID',
      key: 'orderId',
      render: (_, row) => (
        <a css={css(styles.orderLink)} onClick={() => openOrder(row)}>{row.orderId}</a>
      ),
    },
    // Customer Name = originating (pickup) company's name — DBA if present,
    // otherwise the legal licensee name (Danielle 2026-06-02). Positioned
    // between Order ID and Customer Invoice. Propagates to Recent Rejections
    // and Recent Exceptions via the spreads of baseColumns below.
    { title: 'Customer Name', key: 'customerName', render: (_, row) => get(row, 'data.pickupCompany.DBA') || get(row, 'data.pickupCompany.Licensee', '') || '' },
    { title: 'Customer Invoice', key: 'invoice', render: (_, row) => get(row, 'data.billing.invoice', '') || '' },
    { title: 'Delivery Date', key: 'deliveryDate', render: (_, row) => fmtDate(get(row, 'data.order.deliveryDate') || row.delivery_date || row.deliveryDate) },
    { title: 'Dest. Name', key: 'destName', render: (_, row) => get(row, 'data.destCompany.Licensee', '') || '' },
    { title: 'DBA', key: 'dba', render: (_, row) => get(row, 'data.destCompany.DBA', '') || '' },
    { title: 'Manifest', key: 'manifest', render: renderManifest },
    { title: 'Status', key: 'status', render: (_, row) => get(getStatusIcon(row, { isAdmin: true }), 'title', '') },
  ];

  const rejectionColumns = [
    ...baseColumns,
    {
      title: 'Rejection Reason',
      key: 'rejectionReason',
      render: (_, row) => {
        const full = getRejectionReason(row);
        if (!full) return '';
        return (
          <Tooltip title={full} placement="topLeft">
            <span>{truncate(full, REJECTION_TEXT_LIMIT)}</span>
          </Tooltip>
        );
      },
    },
  ];

  const exceptionColumns = [
    ...baseColumns,
    {
      title: 'Exception Reason',
      key: 'exceptionReason',
      render: (_, row) => {
        const full = getExceptionReason(row);
        if (!full) return '';
        return (
          <Tooltip title={full} placement="topLeft">
            <span>{truncate(full, REJECTION_TEXT_LIMIT)}</span>
          </Tooltip>
        );
      },
    },
  ];

  // Returns: show the return date before the order ID link (per Joel's request).
  // Reconciliation Returns Scheduled (formerly "Payment Returns Scheduled") —
  // Customer Invoice and Manifest were removed per Danielle's spec.
  // When "Customer Pickup" is flagged on the return order, the Return Date cell
  // shows "CUSTOMER PICKUP" instead of a date (Danielle 2026-05-19).
  const returnDateColumn = {
    title: 'Return Date',
    key: 'returnDate',
    render: (_, row) => get(row, 'data.order.customerPickup')
      ? 'CUSTOMER PICKUP'
      : fmtDate(get(row, 'data.order.deliveryDate') || row.delivery_date || row.deliveryDate),
  };
  const orderIdColumn = {
    title: 'Order ID',
    key: 'orderId',
    render: (_, row) => (
      <a css={css(styles.orderLink)} onClick={() => openOrder(row)}>{row.orderId}</a>
    ),
  };
  // Quick Link to the signed reconciliation PDF (reuses the recon file stored on
  // the cash_return order — same source the Reconciliation list uses). The
  // displayed Recon ID string was removed per Danielle 2026-05-20 — keep only
  // the Link button.
  const reconIdColumn = {
    title: 'Recon ID',
    key: 'reconId',
    render: (_, row) => {
      const url = getReconUrl(get(row, 'data.recon.file'));
      if (!url) return '';
      return (
        <a css={css(styles.orderLink)} href={url} target="_blank" rel="noopener noreferrer">Link</a>
      );
    },
  };
  const pickupCol = { title: 'Pickup', key: 'pickup', render: (_, row) => get(row, 'data.pickupCompany.Licensee', '') || '' };
  const destCol = { title: 'Destination', key: 'destName', render: (_, row) => get(row, 'data.destCompany.Licensee', '') || '' };
  const dbaCol = { title: 'DBA', key: 'dba', render: (_, row) => get(row, 'data.destCompany.DBA', '') || '' };
  const statusCol = { title: 'Status', key: 'status', render: (_, row) => get(getStatusIcon(row, { isAdmin: true }), 'title', '') };

  const reconReturnColumns = [returnDateColumn, orderIdColumn, reconIdColumn, pickupCol, destCol, dbaCol, statusCol];
  const rejectionReturnColumns = [returnDateColumn, orderIdColumn, pickupCol, destCol, dbaCol, statusCol];

  const scheduledPaymentPickupColumns = [
    {
      title: 'Pickup Date',
      key: 'pickupDate',
      render: (_, row) => fmtDate(
        get(row, 'data.order.pickupDate')
          || get(row, 'data.order.deliveryDate')
          || row.delivery_date
          || row.deliveryDate
      ),
    },
    {
      title: 'Order ID',
      key: 'orderId',
      render: (_, row) => (
        <a css={css(styles.orderLink)} onClick={() => openOrder(row)}>{row.orderId}</a>
      ),
    },
    { title: 'Customer Invoice', key: 'invoice', render: (_, row) => get(row, 'data.billing.invoice', '') || '' },
    { title: 'Pickup', key: 'pickup', render: (_, row) => get(row, 'data.pickupCompany.Licensee', '') || '' },
    { title: 'Destination', key: 'destName', render: (_, row) => get(row, 'data.destCompany.Licensee', '') || '' },
    { title: 'DBA', key: 'dba', render: (_, row) => get(row, 'data.destCompany.DBA', '') || '' },
    { title: 'Amount', key: 'amount', render: (_, row) => get(row, 'data.billing.amount', '') || '' },
    {
      title: 'Method',
      key: 'method',
      render: (_, row) => get(row, 'data.billing.methodSelect') || get(row, 'data.billing.method') || '',
    },
    {
      title: 'Bag or Check #',
      key: 'bagOrCheck',
      render: (_, row) => get(row, 'data.billing.methodSelectId', '') || '',
    },
  ];

  const topLocationColumns = [
    { title: 'License #', dataIndex: 'licenseNumber', key: 'licenseNumber', className: 'license-col' },
    { title: 'Destination Name', dataIndex: 'destName', key: 'destName' },
    { title: 'DBA', dataIndex: 'dba', key: 'dba' },
    { title: '# of Deliveries', dataIndex: 'count', key: 'count' },
  ];

  const topCustomerColumns = [
    { title: 'License #', dataIndex: 'licenseNumber', key: 'licenseNumber', className: 'license-col' },
    { title: 'Customer Name', dataIndex: 'customerName', key: 'customerName' },
    { title: 'DBA', dataIndex: 'dba', key: 'dba' },
    { title: '# of Deliveries', dataIndex: 'count', key: 'count' },
  ];

  const dueCellRender = (mileage, due) => (
    <span css={due ? css(styles.dueCell) : null} style={{ display: 'inline-block', padding: due ? '2px 8px' : 0, borderRadius: due ? 4 : 0 }}>
      {Number.isFinite(mileage) ? mileage.toLocaleString() : '—'}
    </span>
  );

  const oilInterval = intervals.oil || 3500;
  const tireInterval = intervals.tires || 35000;
  const brakeInterval = intervals.brakes || 35000;

  const vehicleColumns = [
    {
      title: 'Vehicle',
      key: 'vehicle',
      render: (_, r) => (
        <div>
          <div>{r.name}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>{r.plate}</div>
        </div>
      ),
    },
    {
      title: 'Reg Expiration',
      key: 'regExpiration',
      render: (_, r) => (
        <span
          css={r.regExpired ? css(styles.dueCell) : null}
          style={{ display: 'inline-block', padding: r.regExpired ? '2px 8px' : 0, borderRadius: r.regExpired ? 4 : 0 }}
        >
          {r.regExpiration ? moment(r.regExpiration).format('L') : '—'}
        </span>
      ),
    },
    { title: 'Total Mileage', key: 'mileage', render: (_, r) => (r.mileage || 0).toLocaleString() },
    { title: 'Last Oil Change', key: 'mileageLoc', render: (_, r) => r.mileageLoc ? r.mileageLoc.toLocaleString() : '—' },
    {
      title: `Oil Change Needed (${oilInterval.toLocaleString()} mi)`,
      key: 'sinceOil',
      render: (_, r) => dueCellRender(r.sinceOil, r.oilDue),
    },
    { title: 'Last Tire Change', key: 'mileageTirechange', render: (_, r) => r.mileageTirechange ? r.mileageTirechange.toLocaleString() : '—' },
    {
      title: `Tire Change Needed (${tireInterval.toLocaleString()} mi)`,
      key: 'sinceTires',
      render: (_, r) => dueCellRender(r.sinceTires, r.tiresDue),
    },
    { title: 'Last Brake Job', key: 'mileageBrakes', render: (_, r) => r.mileageBrakes ? r.mileageBrakes.toLocaleString() : '—' },
    {
      title: `Brake Change Needed (${brakeInterval.toLocaleString()} mi)`,
      key: 'sinceBrakes',
      render: (_, r) => dueCellRender(r.sinceBrakes, r.brakesDue),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        // Joel 2026-05-12: stack Update Mileage ABOVE the Add Maintenance button.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
          <Button size="small" onClick={() => openMileageModal(r)}>Update Mileage</Button>
          <Button size="small" icon="plus" onClick={() => openMaintModal(r)}>Add Maintenance</Button>
        </div>
      ),
    },
  ];

  // Card #242 — the Drivers widget. Colour is applied per DATE CELL, not per
  // row, because the two licence types are tracked independently. `severity`
  // ('red' <= 7 days or already expired, 'orange' <= 30 days, null otherwise)
  // is computed server-side; this does no date math, matching the Reg
  // Expiration cell above.
  const expCellRender = (value, severity) => {
    const style = severity === 'red' ? styles.dueCell : severity === 'orange' ? styles.warnCell : null;
    return (
      <span
        css={style ? css(style) : null}
        style={{ display: 'inline-block', padding: style ? '2px 8px' : 0, borderRadius: style ? 4 : 0 }}
      >
        {value ? moment(value, 'YYYY-MM-DD').format('L') : '—'}
      </span>
    );
  };

  const driverLicenseColumns = [
    { title: 'Driver Name', key: 'name', render: (_, r) => r.name || '—' },
    { title: 'Phone Number', key: 'phone', render: (_, r) => r.phone || '—' },
    { title: 'Driver License Number', key: 'lic', render: (_, r) => r.lic || '—' },
    { title: 'DL Exp', key: 'licExp', render: (_, r) => expCellRender(r.licExp, r.licExpSeverity) },
    { title: 'Occupation License Number', key: 'occLic', render: (_, r) => r.occLic || '—' },
    { title: 'Occupational License Exp', key: 'occLicExp', render: (_, r) => expCellRender(r.occLicExp, r.occLicExpSeverity) },
  ];

  const routeChartWidth = Math.max(routeRows.length, 6) * CHART_PX_PER_DAY;

  // Rotate route labels -35deg so long route names don't overlap each other
  // along the x-axis (Danielle 2026-05-20). Anchored at end so the rotated
  // text reads upward from the tick.
  const RouteTick = props => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          transform="rotate(-35)"
          fill="rgba(0,0,0,0.65)"
          style={{ fontSize: 12 }}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // "Admin Order Export" (Trello #11 / comment #57). Emits the shared 21-column
  // layout (admin-order-export.js) for live orders only — getOrdersInRange
  // already filters deleted=0. Honors the All/Internal/External filter.
  // The "Canceled/Deleted Orders" report now lives only in the Reports Tab
  // (Danielle 2026-06-03 — removed from the Admin Dashboard).
  const exportOrders = async () => {
    setExporting(true);
    try {
      const [orders, recons] = await Promise.all([
        getAdminOrdersInRange({
          range: topRange,
          orderDef: orderDefParam,
        }),
        // No date filter: a recon for an in-range order may have been created
        // outside the window, so fetch all to resolve every Reconciliation
        // Order ID linkage.
        getReconList({}),
      ]);
      const reconMap = buildReconMap(recons);
      const rows = buildAdminExportRows(orders, { reconMap });
      const [start, end] = topRange;
      exportToCsv(`AdminOrderExport-${start.format('YYYY-MM-DD')}-to-${end.format('YYYY-MM-DD')}.csv`, rows);
    } catch (err) {
      message.error(`Failed to export orders: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const runLifecycleReport = async () => {
    if (!userEmail) {
      message.error('Your account has no email on file — cannot send Lifecycle Report.');
      return;
    }
    setLifecycleLoading(true);
    try {
      await getLifecycleReport({
        selectedDate: [topRange[0].toISOString(), topRange[1].toISOString()],
        emails: [userEmail],
      });
      message.success(`Lifecycle Report has started. You will receive an email (${userEmail}) when it is completed.`);
    } catch (err) {
      message.error(`Failed to start Lifecycle Report: ${err.message}`);
    } finally {
      setLifecycleLoading(false);
    }
  };

  // Wrap a report table in a fixed-height scrollable container that defaults
  // to the line limit from DEFAULT_LIMITS, with a "Show all" checkbox in the
  // card's extra that uncaps both the row count and the container height.
  // `extraNode` (optional) renders next to the Show all toggle — used by the
  // Reconciliation Returns container to host its Customer Pickup filter
  // (Danielle 2026-05-20).
  const renderReportSection = (title, data, columns, limitKey, rowKey = 'id', extraNode = null) => {
    // Card #277 — the one search box at the top of the page narrows every report below it.
    // These columns are all `{ title, key, render }` with no dataIndex, so the match has to
    // be made against what each cell drew; the tables are a handful of rows each, which is
    // what makes that affordable here.
    const matching = searchTable(columns, data || [], search, { useRender: true });
    const isShowingAll = !!showAll[limitKey];
    const visible = isShowingAll ? matching : matching.slice(0, DEFAULT_LIMITS[limitKey] || 5);
    return (
      <Card
        title={title}
        css={css(styles.section, styles.sectionCenteredHead)}
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {extraNode}
            <Checkbox
              checked={isShowingAll}
              onChange={e => setShowAll(s => ({ ...s, [limitKey]: e.target.checked }))}
              css={css(styles.showAllToggle)}
            >
              Show all{matching && matching.length ? ` (${matching.length})` : ''}
            </Checkbox>
          </div>
        }
      >
        <div css={css(isShowingAll ? styles.reportScrollAll : styles.reportScroll)}>
          <Table
            columns={columns}
            dataSource={visible}
            rowKey={rowKey}
            pagination={false}
            size="small"
          />
        </div>
      </Card>
    );
  };

  if (!userId) return null;

  return (
    <div css={css(styles.container)}>
      <div css={css(styles.titleRow)}>
        <div css={css(styles.title)}>Admin Dashboard</div>
        <Button
          onClick={refreshAll}
          icon="reload"
          loading={topLoading || recentLoading || returnsLoading || bottomLoading || vehiclesLoading}
          css={css(styles.refreshBtn)}
        >
          Refresh
        </Button>
      </div>

      <div css={css(styles.header)}>
        <div css={css(styles.headerLeft)}>
          <Input.Search
            placeholder="Search every table on this page"
            value={search}
            allowClear
            style={{ maxWidth: 320 }}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div css={css(styles.headerRight)}>
          {internalDistEnabled && (
            <Select
              value={orderDef}
              onChange={setOrderDef}
              style={{ minWidth: 140 }}
              aria-label="Order definition filter"
            >
              <Select.Option value="all">All Orders</Select.Option>
              <Select.Option value="int">Internal Orders</Select.Option>
              <Select.Option value="ext">External Orders</Select.Option>
            </Select>
          )}
          <DatePicker.RangePicker
            value={topRange}
            onChange={v => v && v[0] && v[1] && setTopRange(v)}
            allowClear={false}
          />
          <div css={css(styles.headerActions)}>
            {showAdmin('ordersReport') && (
              <Button onClick={() => exportOrders()} icon="download" loading={exporting} css={css(styles.exportBtn)}>Admin Order Export</Button>
            )}
            {showAdmin('lifecycleReport') && (
              <Button onClick={runLifecycleReport} icon="profile" loading={lifecycleLoading} css={css(styles.exportBtn)}>Lifecycle Report</Button>
            )}
          </div>
        </div>
      </div>

      <Spin spinning={topLoading}>
        {showAdmin('kpis') && (
        <Row type="flex" gutter={12} css={css(styles.kpiRow)}>
          {KPI_DEFS.map(def => (
            <Col key={def.key} xs={12} sm={12} md={6}>
              <Card css={css(styles.kpiCard)}>
                <div css={css(styles.kpiLabel)}>{def.title}</div>
                <div css={css(styles.kpiValue)}>{counts[def.key] || 0}</div>
              </Card>
            </Col>
          ))}
        </Row>
        )}

        {showAdmin('deliveriesByRoute') && (
        <Card title="# of Deliveries per Route" css={css(styles.section)}>
          <div css={css(styles.chartScroll)}>
            <div css={css(styles.chartInner)} style={{ width: routeChartWidth }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={routeRows} margin={{ top: 8, right: 32, left: 8, bottom: 64 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="routeName"
                    interval={0}
                    tick={<RouteTick />}
                    height={70}
                    padding={{ left: 60, right: 30 }}
                  />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip />
                  <Legend />
                  <Line type="linear" dataKey="count" stroke="#52c41a" name="# of Deliveries" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
        )}
      </Spin>

      <Spin spinning={recentLoading}>
        {showAdmin('recentCompleted') && renderReportSection('Recent Completed Deliveries', recent.completed, baseColumns, 'completed')}
        {showAdmin('recentRejections') && renderReportSection('Recent Rejections', recent.rejected, rejectionColumns, 'rejected')}
        {showAdmin('recentExceptions') && renderReportSection('Recent Exceptions', recent.exceptions, exceptionColumns, 'exceptions')}
      </Spin>

      <Spin spinning={returnsLoading}>
        {/* Order locked by Joel 2026-05-12: Reconciliation → Rejection → Payment Pickups Made
            (renamed from "Scheduled Payment Pickups" per same comment). */}
        {showAdmin('reconReturns') && renderReportSection(
          'Reconciliation Returns Scheduled',
          // Apply the Customer Pickup / Returns filter (default Returns) per
          // Danielle 2026-05-20.
          (() => {
            const all = returns.cashReturns || [];
            if (reconReturnFilter === 'all') return all;
            if (reconReturnFilter === 'customerPickups') {
              return all.filter(o => !!get(o, 'data.order.customerPickup'));
            }
            // 'returns' (default) — exclude customer-pickup-flagged orders
            return all.filter(o => !get(o, 'data.order.customerPickup'));
          })(),
          reconReturnColumns,
          'cashReturns',
          'id',
          <Select
            size="small"
            value={reconReturnFilter}
            onChange={setReconReturnFilter}
            style={{ minWidth: 160 }}
            aria-label="Reconciliation return filter"
          >
            <Select.Option value="all">All</Select.Option>
            <Select.Option value="customerPickups">Customer Pickups</Select.Option>
            <Select.Option value="returns">Returns</Select.Option>
          </Select>
        )}
        {showAdmin('rejectionReturns') && renderReportSection(
          'Rejection Returns Scheduled',
          // Same Customer Pickup / Returns filter as Reconciliation Returns
          // (Joel 2026-05-21). Default = Returns (exclude customer pickups).
          (() => {
            const all = returns.rejectionReturns || [];
            if (rejReturnFilter === 'all') return all;
            if (rejReturnFilter === 'customerPickups') {
              return all.filter(o => !!get(o, 'data.order.customerPickup'));
            }
            return all.filter(o => !get(o, 'data.order.customerPickup'));
          })(),
          rejectionReturnColumns,
          'rejectionReturns',
          'id',
          <Select
            size="small"
            value={rejReturnFilter}
            onChange={setRejReturnFilter}
            style={{ minWidth: 160 }}
            aria-label="Rejection return filter"
          >
            <Select.Option value="all">All</Select.Option>
            <Select.Option value="customerPickups">Customer Pickups</Select.Option>
            <Select.Option value="returns">Returns</Select.Option>
          </Select>
        )}
        {showAdmin('paymentPickups') && renderReportSection('Payment Pickups Made', returns.scheduledPaymentPickups, scheduledPaymentPickupColumns, 'scheduledPaymentPickups')}
      </Spin>

      <hr css={css(styles.sectionBreak)} />

      <div css={css(styles.bottomDateRow)}>
        <DatePicker.RangePicker
          value={bottomRange}
          onChange={v => v && v[0] && v[1] && setBottomRange(v)}
          allowClear={false}
          size="small"
          css={css(styles.smallDatePicker)}
        />
      </div>

      <Spin spinning={bottomLoading}>
        {showAdmin('deliveriesByDay') && (
        <Row gutter={12} css={css(styles.section)}>
          <Col xs={24} md={12}>
            <Card
              title="Deliveries by Day of the Week"
              extra={renderDaySelect(lineDays, setLineDays)}
            >
              <div css={css(styles.chartScroll)}>
                <div
                  css={css(styles.chartInner)}
                  style={{ width: Math.max(lineSeriesFiltered.length, CHART_VISIBLE_DAYS) * CHART_PX_PER_DAY }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={lineSeriesFiltered} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={fmtTick} interval={0} />
                      <YAxis allowDecimals={false} />
                      <ChartTooltip labelFormatter={fmtTick} />
                      <Legend />
                      <Line type="monotone" dataKey="delivered" stroke="#52c41a" name="Delivered" />
                      <Line type="monotone" dataKey="rejected" stroke="#f5222d" name="Full Rejection" />
                      <Line type="monotone" dataKey="partial" stroke="#faad14" name="Partial Rejection" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              title="Average Deliveries by Day of the Week"
              extra={renderDaySelect(avgDays, setAvgDays)}
            >
              <div css={css(styles.chartWrap)}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgFiltered} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" interval={0} />
                    <YAxis allowDecimals={true} />
                    <ChartTooltip />
                    <Legend />
                    <Bar dataKey="average" name="Avg Deliveries" fill="#52c41a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>
        )}

        <Row gutter={12} css={css(styles.section)}>
          {showAdmin('topLocations') && (
          <Col xs={24} md={12}>
            <Card title={`Top ${TOP_LOCATIONS_LIMIT} Delivery Locations`} css={css(styles.licenseNoWrap)}>
              <Table columns={topLocationColumns} dataSource={searchTable(topLocationColumns, topLocations, search, { useRender: true })} rowKey="licenseNumber" pagination={false} size="small" />
            </Card>
          </Col>
          )}
          {showAdmin('topCustomers') && (
          <Col xs={24} md={12}>
            <Card title={`Top ${TOP_CUSTOMERS_LIMIT} Customers`} css={css(styles.licenseNoWrap)}>
              <Table columns={topCustomerColumns} dataSource={searchTable(topCustomerColumns, topCustomers, search, { useRender: true })} rowKey="licenseNumber" pagination={false} size="small" />
            </Card>
          </Col>
          )}
        </Row>
      </Spin>

      {showAdmin('vehicleMaintenance') && (
      <Spin spinning={vehiclesLoading}>
        {/* Per Joel 2026-05-12 the top-right "Add Maintenance" button is removed —
            users add maintenance via the per-row button in the Actions column. */}
        <Card
          title="Vehicle Maintenance"
          css={css(styles.section)}
        >
          <Table
            columns={vehicleColumns}
            dataSource={searchTable(vehicleColumns, vehicleRows, search, { useRender: true })}
            rowKey="vehicleId"
            pagination={false}
            size="small"
            scroll={{ x: 1400 }}
          />
        </Card>
      </Spin>
      )}

      {/* Card #242 — sits directly below Vehicle Maintenance per Joel's
          annotated mockup, and lists EVERY driver (blank dates render as an
          uncoloured em dash). Hidden entirely unless the licenseExpiry.enabled
          master switch is on. */}
      {licenseExpiryEnabled && showAdmin('driverLicenses') && (
      <Spin spinning={driverLicensesLoading}>
        <Card
          title="Drivers"
          css={css(styles.section)}
        >
          <Table
            columns={driverLicenseColumns}
            dataSource={searchTable(driverLicenseColumns, driverLicenseRows, search, { useRender: true })}
            rowKey="driverId"
            pagination={false}
            size="small"
            scroll={{ x: 900 }}
            locale={{ emptyText: 'No drivers' }}
          />
        </Card>
      </Spin>
      )}

      <Modal
        title="Update Mileage"
        visible={mileageModal.visible}
        onOk={saveMileage}
        onCancel={closeMileageModal}
        okText="Save"
      >
        {mileageModal.vehicle && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <strong>{mileageModal.vehicle.name}</strong> {mileageModal.vehicle.plate ? `(${mileageModal.vehicle.plate})` : ''}
            </div>
            <div style={{ marginBottom: 8 }}>
              Current mileage: {(mileageModal.vehicle.mileage || 0).toLocaleString()}
            </div>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
              // Coerce to a number or null — Ant's InputNumber emits strings for
              // partial inputs and undefined when fully cleared, which crashed
              // the page on rapid backspaces (Danielle 2026-05-12).
              value={mileageModal.value == null || mileageModal.value === '' ? null : Number(mileageModal.value)}
              onChange={v => {
                const next = v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
                setMileageModal(s => ({ ...s, value: next }));
              }}
              placeholder="New mileage"
            />
          </div>
        )}
      </Modal>

      <MaintForm
        title="Add Maintenance"
        {...maintModal}
      />

      <OrderModal editModal={editModal} onClose={closeOrder} />
    </div>
  );
};
